const MIN_HOSTED_SESSIONS = 3
const MAX_UNCLICKED_EDITOR_VIEWS = 3
export const MARKER_LIFETIME_MS = 14 * 24 * 60 * 60 * 1000

// Stored on the user's personalInformation so seen/dismissed state follows the
// account across browsers, not the device.
export const DISCOVERY_STATE_FIELD = 'courseConversionDiscovery'

const WHITE_LABEL_PLAN = 'white-label'

// Stored values: onboarding saves School / Business / Others; legacy accounts hold education / work / community / others.
const COURSE_AUTHOR_PURPOSES = ['school', 'education']
// Business covers Trainer / L&D, so the purpose alone does not tell us either way.
const PURPOSES_WITHOUT_SEGMENT_SIGNAL = ['business', 'work', 'others', 'community']
// HR is included because L&D usually sits in HR.
const COURSE_AUTHOR_PROFESSIONS = ['trainer / l&d', 'hr', 'professor / lecturer', 'teacher']
const PROFESSIONS_WITHOUT_SEGMENT_SIGNAL = ['others']

const normalise = (value) => (typeof value === 'string' ? value.trim().toLowerCase() : '')

export const isWhiteLabelPlan = ({ planGroup, userPlan } = {}) => (
  planGroup === WHITE_LABEL_PLAN || userPlan === WHITE_LABEL_PLAN
)

export const hasEnoughHostingHistory = (hostedSessionCount) => (
  Number.isFinite(hostedSessionCount) && hostedSessionCount >= MIN_HOSTED_SESSIONS
)

/** True/false for the training, education or L&D segment; null when the onboarding answers do not say. */
const matchesCourseAuthorUseCase = (personalInformation) => {
  const profession = normalise(personalInformation?.userProfession)
  if (profession && !PROFESSIONS_WITHOUT_SEGMENT_SIGNAL.includes(profession)) {
    return COURSE_AUTHOR_PROFESSIONS.includes(profession)
  }
  const purpose = normalise(personalInformation?.useAhaSlidesFor)
  if (!purpose || PURPOSES_WITHOUT_SEGMENT_SIGNAL.includes(purpose)) return null
  return COURSE_AUTHOR_PURPOSES.includes(purpose)
}

export const readDiscoveryState = (personalInformation) => {
  const stored = personalInformation?.[DISCOVERY_STATE_FIELD]
  const state = stored && typeof stored === 'object' ? stored : {}
  return {
    eligibleAt: state.eligibleAt || null,
    firstSeenAt: state.firstSeenAt || null,
    unclickedViews: Number.isFinite(state.unclickedViews) ? state.unclickedViews : 0,
    discoveredAt: state.discoveredAt || null,
    nudgeSeenAt: state.nudgeSeenAt || null,
    usedAt: state.usedAt || null,
  }
}

const toTime = (value) => {
  const time = new Date(value).getTime()
  return Number.isNaN(time) ? null : time
}

export const shouldShowDiscoveryMarkers = (state, now = Date.now()) => {
  if (!state || state.discoveredAt || state.usedAt) return false
  if (state.unclickedViews >= MAX_UNCLICKED_EDITOR_VIEWS) return false
  const firstSeenTime = state.firstSeenAt ? toTime(state.firstSeenAt) : null
  return firstSeenTime === null || now - firstSeenTime < MARKER_LIFETIME_MS
}

const stampOnce = (state, key, now) => ({
  ...state,
  [key]: state[key] || new Date(now).toISOString(),
})

export const recordDiscoveryView = (state, now = Date.now()) => ({
  ...stampOnce(state, 'firstSeenAt', now),
  unclickedViews: state.unclickedViews + 1,
})

// The hosted-session count only grows, so once it clears the threshold it never needs asking again.
export const recordEligible = (state, now = Date.now()) => stampOnce(state, 'eligibleAt', now)

export const recordDiscovered = (state, now = Date.now()) => stampOnce(state, 'discoveredAt', now)

export const recordNudgeSeen = (state, now = Date.now()) => stampOnce(state, 'nudgeSeenAt', now)

export const recordUsed = (state, now = Date.now()) => stampOnce(recordDiscovered(state, now), 'usedAt', now)

export const presentationHasSlides = (presentation) => (
  !!presentation && Array.isArray(presentation.slides) && presentation.slides.length > 0
)

const presentationHasBeenHosted = (presentation) => !!presentation?.eventStartedAt

// Every modal family the editor mounts plus the onboarding tour card: the nudge must never stack on one.
const OVERLAY_SELECTORS = [
  '.aha-modal',
  '.modal.show',
  '.ant-modal-wrap',
  '.onboarding-tooltip',
]

const isDisplayed = (element) => !element.hidden && element.style.display !== 'none'

export const isAnotherOverlayOpen = (root) => {
  if (!root || typeof root.querySelectorAll !== 'function') return false
  return OVERLAY_SELECTORS.some((selector) => Array.from(root.querySelectorAll(selector)).some(isDisplayed))
}

export const passesNudgeAudienceGates = ({ presentation, personalInformation, discoveryState }) => {
  if (!presentationHasBeenHosted(presentation) || !presentationHasSlides(presentation)) return false
  if (matchesCourseAuthorUseCase(personalInformation) === false) return false
  return !discoveryState.nudgeSeenAt && !discoveryState.usedAt
}
